import React from 'react';

const formatCurrency = (value) => Number(value || 0).toLocaleString('ru-RU');

const SummaryTable = ({ title, rows, columns, emptyText = 'Нет данных' }) => (
    <div className="report-summary-block">
        <h4>{title}</h4>
        {rows?.length ? (
            <table className="ant-table universal-table report-summary-table">
                <thead>
                    <tr>
                        {columns.map(col => <th key={col.key}>{col.title}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <tr key={row.key || idx}>
                            {columns.map(col => (
                                <td key={col.key}>
                                    {col.render ? col.render(row) : (row[col.key] ?? '-')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        ) : (
            <p className="report-summary-empty">{emptyText}</p>
        )}
    </div>
);

const SalesSummaryPanel = ({ summary }) => {
    const moneyCol = { key: 'revenue', title: 'Выручка (₽)', render: (r) => formatCurrency(r.revenue) };
    const qtyCol = { key: 'soldQty', title: 'Продано (шт)' };
    const ordersCol = { key: 'ordersCount', title: 'Заказов' };

    return (
        <div className="report-summary-panel">
            <div className="report-summary-cards">
                <div className="report-summary-card">
                    <div className="report-summary-card-label">Общая выручка (прибыль от продаж)</div>
                    <div className="report-summary-card-value">{formatCurrency(summary.totals?.revenue)} ₽</div>
                </div>
                <div className="report-summary-card">
                    <div className="report-summary-card-label">Продано единиц</div>
                    <div className="report-summary-card-value">{summary.totals?.soldQty || 0}</div>
                </div>
                <div className="report-summary-card">
                    <div className="report-summary-card-label">Завершённых заказов</div>
                    <div className="report-summary-card-value">{summary.totals?.ordersCount || 0}</div>
                </div>
            </div>

            <SummaryTable
                title="Разбивка по типу оплаты"
                rows={summary.byPaymentType}
                columns={[
                    { key: 'label', title: 'Тип оплаты' },
                    moneyCol,
                    qtyCol,
                    ordersCol,
                ]}
            />

            <SummaryTable
                title="Разбивка по типу товара"
                rows={summary.byProductType}
                columns={[
                    { key: 'label', title: 'Тип товара' },
                    moneyCol,
                    qtyCol,
                ]}
            />

            <SummaryTable
                title="Разбивка по категориям"
                rows={summary.byCategory}
                columns={[
                    { key: 'label', title: 'Категория' },
                    moneyCol,
                    qtyCol,
                ]}
            />

            <SummaryTable
                title="Разбивка по магазинам"
                rows={summary.byStore}
                columns={[
                    { key: 'label', title: 'Магазин' },
                    moneyCol,
                    qtyCol,
                    ordersCol,
                ]}
            />

            <SummaryTable
                title="Статистика по месяцам"
                rows={summary.byMonth}
                columns={[
                    { key: 'label', title: 'Месяц' },
                    moneyCol,
                    qtyCol,
                    ordersCol,
                ]}
            />

            <SummaryTable
                title="Статистика по годам"
                rows={summary.byYear}
                columns={[
                    { key: 'label', title: 'Год' },
                    moneyCol,
                    qtyCol,
                    ordersCol,
                ]}
            />
        </div>
    );
};

const StockSummaryPanel = ({ summary }) => {
    const qtyCol = { key: 'stockQty', title: 'Остаток (шт)' };
    const valueCol = { key: 'stockValue', title: 'Остаток (₽)', render: (r) => formatCurrency(r.stockValue) };
    const positionsCol = { key: 'positions', title: 'Позиций' };

    return (
        <div className="report-summary-panel">
            <div className="report-summary-cards">
                <div className="report-summary-card stock">
                    <div className="report-summary-card-label">Общий остаток</div>
                    <div className="report-summary-card-value">{summary.totals?.stockQty || 0} шт</div>
                </div>
                <div className="report-summary-card stock">
                    <div className="report-summary-card-label">Сумма остатков</div>
                    <div className="report-summary-card-value">{formatCurrency(summary.totals?.stockValue)} ₽</div>
                </div>
                <div className="report-summary-card stock">
                    <div className="report-summary-card-label">Позиций к пополнению</div>
                    <div className="report-summary-card-value">{summary.replenishment?.items?.length || 0}</div>
                </div>
            </div>

            <SummaryTable
                title="Остатки по типу товара"
                rows={summary.byProductType}
                columns={[
                    { key: 'label', title: 'Тип товара' },
                    qtyCol,
                    valueCol,
                    positionsCol,
                ]}
            />

            <SummaryTable
                title="Остатки по категориям"
                rows={summary.byCategory}
                columns={[
                    { key: 'label', title: 'Категория' },
                    qtyCol,
                    valueCol,
                    positionsCol,
                ]}
            />

            <SummaryTable
                title="Остатки по магазинам"
                rows={summary.byStore}
                columns={[
                    { key: 'label', title: 'Магазин' },
                    qtyCol,
                    valueCol,
                    positionsCol,
                ]}
            />

            <SummaryTable
                title="Пополнение по типу товара"
                rows={summary.replenishment?.byProductType}
                columns={[
                    { key: 'label', title: 'Тип товара' },
                    { key: 'stockQty', title: 'Нужно пополнить (шт)' },
                    positionsCol,
                ]}
                emptyText="Нет позиций, требующих пополнения"
            />

            <SummaryTable
                title="Пополнение по категориям и магазинам"
                rows={summary.replenishment?.byCategory}
                columns={[
                    { key: 'label', title: 'Категория' },
                    { key: 'store', title: 'Магазин' },
                    { key: 'type', title: 'Тип' },
                    { key: 'stockQty', title: 'Нужно пополнить (шт)' },
                    positionsCol,
                ]}
                emptyText="Нет категорий, требующих пополнения"
            />

            <SummaryTable
                title="Детальный список позиций к пополнению"
                rows={summary.replenishment?.items}
                columns={[
                    { key: 'store', title: 'Магазин' },
                    { key: 'type', title: 'Тип', render: (r) => ({ shoe: 'Обувь', clothes: 'Одежда', toy: 'Игрушки' }[r.type] || r.type) },
                    { key: 'category', title: 'Категория' },
                    { key: 'name', title: 'Товар' },
                    { key: 'size', title: 'Размер' },
                    { key: 'quantity', title: 'Остаток' },
                    { key: 'replenishQty', title: 'Пополнить' },
                    { key: 'reason', title: 'Причина' },
                ]}
                emptyText="Все остатки в норме"
            />
        </div>
    );
};

const ReportSummaryPanel = ({ summary, reportMode }) => {
    if (!summary) return null;
    if (reportMode === 'sales_summary') return <SalesSummaryPanel summary={summary} />;
    if (reportMode === 'stock_summary') return <StockSummaryPanel summary={summary} />;
    return null;
};

export default ReportSummaryPanel;
